import { useState } from 'react';
import { apiClient } from '@/lib/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ContractViolation, PublishDatasetResponse } from '../types/pipeline';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Copy, Check, ExternalLink, ShieldAlert, AlertTriangle } from 'lucide-react';

interface PublishDatasetDialogProps {
  pipelineId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Accepts one column per line or comma-separated; trims, drops blanks, dedupes. */
function parseColumnList(raw: string): string[] {
  const seen = new Set<string>();
  for (const token of raw.split(/[\n,]/)) {
    const name = token.trim();
    if (name) seen.add(name);
  }
  return Array.from(seen);
}

export function PublishDatasetDialog({
  pipelineId,
  open,
  onOpenChange,
}: PublishDatasetDialogProps) {
  const [isPublic, setIsPublic] = useState(true);
  const [expiresAt, setExpiresAt] = useState('');
  const [tableName, setTableName] = useState('');
  const [gatedOnly, setGatedOnly] = useState(false);
  const [columnsText, setColumnsText] = useState('');
  const [acknowledgedPiiText, setAcknowledgedPiiText] = useState('');
  const [violations, setViolations] = useState<ContractViolation[] | null>(null);
  const [actualColumns, setActualColumns] = useState<string[] | null>(null);
  const [publishedData, setPublishedData] = useState<PublishDatasetResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  const columns = parseColumnList(columnsText);
  const acknowledgedPii = parseColumnList(acknowledgedPiiText);
  const contractRequired = gatedOnly;
  const canPublish = !contractRequired || columns.length > 0;

  const publishMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<{ dataset: PublishDatasetResponse }>(
        `/pipelines/${pipelineId}/publish`,
        {
          isPublic,
          expiresAt: expiresAt || undefined,
          tableName: tableName || undefined,
          gatedOnly,
          columnContract:
            columns.length > 0
              ? {
                  columns,
                  acknowledgedPii: acknowledgedPii.length > 0 ? acknowledgedPii : undefined,
                }
              : undefined,
        },
      );
      return response.dataset;
    },
    onMutate: () => {
      setViolations(null);
      setActualColumns(null);
    },
    onSuccess: (data) => {
      setPublishedData(data);
      queryClient.invalidateQueries({ queryKey: ['pipelines', 'data', pipelineId] });
      toast.success('Dataset published successfully');
    },
    onError: (error: any) => {
      // A 422 carries the contract violations plus the columns the pipeline
      // actually produced, so the operator can correct it in one round trip.
      const data = error.response?.data;
      setViolations(Array.isArray(data?.violations) ? data.violations : null);
      setActualColumns(Array.isArray(data?.actualColumns) ? data.actualColumns : null);
      toast.error(data?.message || 'Failed to publish dataset');
    },
  });

  const handlePublish = () => {
    publishMutation.mutate();
  };

  const handleCopyApiKey = async () => {
    if (publishedData?.apiKey) {
      await navigator.clipboard.writeText(publishedData.apiKey);
      setCopied(true);
      toast.success('API key copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyUrl = async () => {
    if (publishedData?.accessUrl) {
      await navigator.clipboard.writeText(publishedData.accessUrl);
      toast.success('Access URL copied to clipboard');
    }
  };

  const handleClose = () => {
    setPublishedData(null);
    setIsPublic(true);
    setExpiresAt('');
    setTableName('');
    setGatedOnly(false);
    setColumnsText('');
    setAcknowledgedPiiText('');
    setViolations(null);
    setActualColumns(null);
    setCopied(false);
    onOpenChange(false);
  };

  // Calculate minimum date (tomorrow)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
        {!publishedData ? (
          <>
            <DialogHeader>
              <DialogTitle>Publish Dataset</DialogTitle>
              <DialogDescription>
                Make your pipeline output accessible via an API endpoint.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Table Name */}
              <div className="space-y-2">
                <Label htmlFor="tableName">Table Name (Optional)</Label>
                <Input
                  id="tableName"
                  placeholder="e.g., sales_report_2024"
                  value={tableName}
                  onChange={(e) => setTableName(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Custom name for this dataset. If not provided, a default name will be used.
                </p>
              </div>

              {/* Public Access */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isPublic"
                  checked={isPublic}
                  onCheckedChange={(checked: boolean) => setIsPublic(checked)}
                />
                <Label htmlFor="isPublic" className="cursor-pointer">
                  Make dataset publicly accessible
                </Label>
              </div>

              {/* Gateway-only access */}
              <div className="rounded-lg border p-3 space-y-2">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="gatedOnly"
                    checked={gatedOnly}
                    onCheckedChange={(checked: boolean) => setGatedOnly(checked)}
                    className="mt-0.5"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="gatedOnly" className="cursor-pointer flex items-center gap-1.5">
                      <ShieldAlert className="h-3.5 w-3.5 text-amber-600" />
                      Serve only through the API gateway
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Required for datasets derived from personal data. The public URL will answer
                      404; only requests carrying the gateway secret are served.
                    </p>
                  </div>
                </div>
              </div>

              {/* Column contract */}
              <div className="space-y-2">
                <Label htmlFor="columns">
                  Approved Columns {contractRequired ? '(Required)' : '(Optional)'}
                </Label>
                <Textarea
                  id="columns"
                  rows={4}
                  placeholder={'kabupaten\nkecamatan\nprogram\nperiode\njumlah_penerima'}
                  value={columnsText}
                  onChange={(e) => setColumnsText(e.target.value)}
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground">
                  One per line or comma-separated. Every publish and every scheduled refresh is
                  checked against this list, and rejected if the output grows or loses a column —
                  the existing data keeps being served instead.
                  {columns.length > 0 && ` ${columns.length} column(s) declared.`}
                </p>
              </div>

              {columns.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="acknowledgedPii">Acknowledged Personal-Data Columns</Label>
                  <Input
                    id="acknowledgedPii"
                    placeholder="e.g., nik, nama_penerima"
                    value={acknowledgedPiiText}
                    onChange={(e) => setAcknowledgedPiiText(e.target.value)}
                    className="font-mono text-xs"
                  />
                  <p className="text-xs text-muted-foreground">
                    Columns matching the personal-data deny-list are rejected unless listed here.
                    Leave empty unless this endpoint genuinely must serve identifiers.
                  </p>
                </div>
              )}

              {/* Contract violations returned by the server */}
              {violations && violations.length > 0 && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-3 space-y-2">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-destructive">
                    <AlertTriangle className="h-4 w-4" />
                    Nothing was published
                  </div>
                  <ul className="space-y-1.5">
                    {violations.map((violation, index) => (
                      <li key={`${violation.code}-${index}`} className="text-xs">
                        <span className="font-mono text-muted-foreground">{violation.code}</span>
                        <span className="block">{violation.message}</span>
                      </li>
                    ))}
                  </ul>
                  {actualColumns && actualColumns.length > 0 && (
                    <div className="space-y-1.5 pt-1">
                      <p className="text-xs text-muted-foreground">
                        Columns the pipeline actually produced:{' '}
                        <span className="font-mono">{actualColumns.join(', ')}</span>
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setColumnsText(actualColumns.join('\n'))}
                      >
                        Use actual columns
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Expiration Date */}
              <div className="space-y-2">
                <Label htmlFor="expiresAt">Expiration Date (Optional)</Label>
                <Input
                  id="expiresAt"
                  type="date"
                  min={minDate}
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Dataset will automatically become unavailable after this date.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handlePublish} disabled={publishMutation.isPending || !canPublish}>
                {publishMutation.isPending ? 'Publishing...' : 'Publish Dataset'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Dataset Published Successfully!</DialogTitle>
              <DialogDescription>
                {publishedData.gatedOnly
                  ? 'This dataset is reachable only through the API gateway.'
                  : 'Your dataset is now accessible via the API key below.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {publishedData.gatedOnly && (
                <div className="rounded-lg border border-amber-500/50 bg-amber-500/5 p-3">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-amber-700 dark:text-amber-500">
                    <ShieldAlert className="h-4 w-4" />
                    Gateway-only dataset
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Register the URL below as the backend endpoint in WSO2 and attach the
                    <span className="font-mono"> X-Gateway-Secret </span>
                    header there. Opening this URL directly returns 404 — that is expected. Hand
                    consumers the WSO2 URL, never this one.
                  </p>
                </div>
              )}

              {/* API Key */}
              <div className="space-y-2">
                <Label>API Key</Label>
                <div className="flex gap-2">
                  <Input value={publishedData.apiKey} readOnly className="font-mono text-xs" />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopyApiKey}
                    title="Copy API key"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Access URL */}
              <div className="space-y-2">
                <Label>{publishedData.gatedOnly ? 'Backend Endpoint (for WSO2)' : 'Access URL'}</Label>
                <div className="flex gap-2">
                  <Input value={publishedData.accessUrl} readOnly className="font-mono text-xs" />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopyUrl}
                    title="Copy access URL"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  {/* Opening a gated URL directly always 404s, so the button is omitted. */}
                  {!publishedData.gatedOnly && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => window.open(publishedData.accessUrl, '_blank')}
                      title="Open in new tab"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Dataset Info */}
              <div className="rounded-lg border p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Table Name:</span>
                  <span className="font-medium">{publishedData.tableName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Record Count:</span>
                  <span className="font-medium">{(publishedData.recordCount ?? 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Access:</span>
                  <span className="font-medium">
                    {publishedData.gatedOnly
                      ? 'Gateway only'
                      : publishedData.isPublic
                        ? 'Public'
                        : 'Private'}
                  </span>
                </div>
                {publishedData.columnContract?.columns && (
                  <div className="flex justify-between text-sm gap-4">
                    <span className="text-muted-foreground shrink-0">Column Contract:</span>
                    <span className="font-mono text-xs text-right">
                      {publishedData.columnContract.columns.join(', ')}
                    </span>
                  </div>
                )}
                {publishedData.expiresAt && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Expires At:</span>
                    <span className="font-medium">
                      {new Date(publishedData.expiresAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Usage Example */}
              <div className="space-y-2">
                <Label>Usage Example (curl)</Label>
                <div className="rounded-lg bg-muted p-3 font-mono text-xs overflow-x-auto">
                  <code>
                    {publishedData.gatedOnly
                      ? `curl -H "X-Gateway-Secret: <secret>" ${publishedData.accessUrl}`
                      : `curl ${publishedData.accessUrl}`}
                  </code>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
